import React, { useState, useRef } from 'react';
import { User, Users, Mail, Phone, Award, Calendar, Edit2, Save, X, Camera } from 'lucide-react';
import { Card, CardContent, Avatar, Button, Badge, WavesLedgerModal } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../services/api';
import styles from './Profile.module.css';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data for profile editing
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });

  // Waves Ledger Modal
  const [showLedgerModal, setShowLedgerModal] = useState(false);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert to base64 for simplicity (in production, you'd upload to a file server)
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;

        try {
          await authApi.updateProfile({ avatar_url: base64 });
          updateUser({ avatar_url: base64 });
          setSuccess('Photo updated successfully!');
          setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
          setError(err.response?.data?.error || 'Failed to update photo');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to process image');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      await authApi.updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
      });

      updateUser({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
      });

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'executive': return 'primary';
      case 'mentor': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>My Profile</h1>
        <p>Manage your personal information and preferences</p>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
      {success && <div className={styles.successMessage}>{success}</div>}

      <div className={styles.content}>
        <div className={styles.mainSection}>
          <Card>
            <CardContent>
              <div className={styles.profileHeader}>
                <div className={styles.avatarSection}>
                  <div className={styles.avatarWrapper}>
                    <Avatar
                      src={user?.avatar_url}
                      name={`${user?.first_name} ${user?.last_name}`}
                      size="xl"
                    />
                    <button
                      className={styles.changePhotoBtn}
                      onClick={handlePhotoClick}
                      disabled={loading}
                    >
                      <Camera size={16} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <span className={styles.photoHint}>Click camera icon to change photo</span>
                </div>
                <div className={styles.profileActions}>
                  {isEditing ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} disabled={loading}>
                        <X size={16} /> Cancel
                      </Button>
                      <Button size="sm" onClick={handleSave} disabled={loading}>
                        <Save size={16} /> {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      <Edit2 size={16} /> Edit Profile
                    </Button>
                  )}
                </div>
              </div>

              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <User size={18} />
                  </div>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Full Name</span>
                    {isEditing ? (
                      <div className={styles.editInputs}>
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          placeholder="First Name"
                          className={styles.input}
                        />
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          placeholder="Last Name"
                          className={styles.input}
                        />
                      </div>
                    ) : (
                      <span className={styles.infoValue}>{user?.first_name} {user?.last_name}</span>
                    )}
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <Mail size={18} />
                  </div>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Email Address</span>
                    <span className={styles.infoValue}>{user?.email}</span>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <Phone size={18} />
                  </div>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Mobile Number</span>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Enter mobile number"
                        className={styles.input}
                      />
                    ) : (
                      <span className={styles.infoValue}>{user?.phone || 'Not set'}</span>
                    )}
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <Award size={18} />
                  </div>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Role</span>
                    <Badge variant={getRoleBadgeVariant(user?.role || '')}>
                      {user?.role?.charAt(0).toUpperCase()}{user?.role?.slice(1)}
                    </Badge>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <Calendar size={18} />
                  </div>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Member Since</span>
                    <span className={styles.infoValue}>{formatDate(user?.created_at)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={styles.sidebar}>
          <Card className={styles.statsCard}>
            <CardContent>
              <h3 className={styles.cardTitle}>Waves Summary</h3>
              <div className={styles.statsList}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Waves Balance</span>
                  <span className={styles.statValue}>{user?.points_balance || 0}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Waves Received</span>
                  <span className={styles.statValue}>{user?.points_received || 0}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Waves Given</span>
                  <span className={styles.statValue}>{user?.points_given || 0}</span>
                </div>
              </div>

              <button
                className={styles.detailsToggle}
                onClick={() => setShowLedgerModal(true)}
              >
                View Details
              </button>
            </CardContent>
          </Card>

          <Card className={styles.mentorCard}>
            <CardContent>
              <h3 className={styles.cardTitle}>
                <Users size={18} /> My Mentor
              </h3>
              {(() => {
                const mentorFirst = user?.manager_first_name || user?.mentor_first_name;
                const mentorLast = user?.manager_last_name || user?.mentor_last_name;
                const mentorAvatar = user?.manager_avatar || user?.mentor_avatar;
                const isSelf = user?.manager_id === user?.id;
                if (!mentorFirst || isSelf) {
                  return <span className={styles.mentorNotAssigned}>Not Assigned</span>;
                }
                return (
                  <div className={styles.mentorRow}>
                    <Avatar src={mentorAvatar} name={`${mentorFirst} ${mentorLast}`} size="md" />
                    <div className={styles.mentorInfo}>
                      <span className={styles.mentorName}>{mentorFirst} {mentorLast}</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Waves Ledger Modal */}
      <WavesLedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
      />
    </div>
  );
};

export default Profile;
